import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findLang, SWORN_LANGUAGES } from "@/data/sworn-languages";

export const Route = createFileRoute("/en/sworn-translator/$language")({
  loader: ({ params }) => {
    const lang = findLang(params.language);
    if (!lang) throw notFound();
    return { lang };
  },
  head: ({ params }) => {
    const lang = findLang(params.language);
    if (!lang) return { meta: [{ title: ctaTitle("Not found", "/en/sworn-translator/$language", "en")}, { name: "robots", content: "noindex" }] };
    const path = `/en/sworn-translator/${lang.slug}`;
    const TITLE = `${lang.en} Sworn Translator — Court-Registered | NYC Thailand`;
    const DESC = `Court-sworn ${lang.en} translator (${lang.registryEn}), ${lang.turnaround}. Sworn seal accepted at destination without extra notary. Get your checklist in minutes.`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/sworn-translator/$language", "en")}, { name: "description", content: ctaDesc(DESC, "/en/sworn-translator/$language", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/sworn-translator/$language", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/sworn-translator/$language", "en")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/sworn-translator/${lang.slug}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `Sworn Translation — ${lang.en}`, priceFromThb: lang.priceFrom,
        steps: [
          { name: "Destination check", text: `Pick the sworn class accepted by the ${lang.en} authority (${lang.registryEn}).` },
          { name: "Sworn translation", text: `Translator registered with ${lang.registryEn}, verifiable number.` },
          { name: "QA + sworn stamp", text: "4-eyes review + Certificate of Accuracy." },
          { name: "MFA + Apostille (optional)", text: "Chain into Thai MFA + Apostille (Hague, in force 15 Dec 2025)." },
          { name: "Worldwide delivery", text: "EMS/DHL/FedEx 2–5 business days." },
        ],
        faqs: [
          { q: `Who certifies ${lang.en} sworn translators?`, a: lang.registryEn },
          { q: "How is this different from certified translation?", a: `A sworn ${lang.en} translation is a legal instrument at destination and does not require a separate notary.` },
          { q: "Pricing and turnaround?", a: `From ฿${lang.priceFrom.toLocaleString()}/page, ${lang.turnaround}. Express 24h +50%.` },
          { q: "Any caveats?", a: lang.notes || "Always verify destination requirements before ordering." },
          { q: "Can you add MFA + Apostille?", a: `Yes — bundling ${lang.en} sworn translation with Thai MFA legalisation and Apostille cuts total turnaround roughly 60% versus running each step separately, because we hand-carry to Chaeng Watthana in parallel with the QA cycle and courier onward via DHL.` },
          { q: "Which document types?", a: `Court judgments, contracts, powers of attorney, wills, corporate affidavits, board resolutions, marriage/divorce/birth certificates, university transcripts, diplomas, medical reports, and patents — all handled by ${lang.en} translators matched to the subject area.` },
        ],
        breadcrumbs: [
          { name: "Home", path: "/en" },
          { name: "Sworn Translator", path: "/en/sworn-translator" },
          { name: lang.en, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">Language not found</h1>      <QuotableStatsStrip cluster="translate" locale="en" variant="light" />
    </main>
  ),
});

function Page() {
  const { lang } = Route.useLoaderData();
  return (
    <ServiceLanding
      badge={`Sworn · ${lang.en}`}
      title={`${lang.en} Sworn Translator`}
      subtitle={`Court-sworn ${lang.en} translation (${lang.registryEn}) — ${lang.notes || "sworn seal accepted at destination without extra notary."}`}
      quickAnswer={{
        q: `Who is a ${lang.en} sworn translator?`,
        a: `A translator sworn under ${lang.registryEn}. Their translation is a legal instrument on its own. From ฿${lang.priceFrom.toLocaleString()}/page, ${lang.turnaround}.`,
      }}
      heroBullets={[
        `Registry: ${lang.registryEn}`,
        `From ฿${lang.priceFrom.toLocaleString()}/page`,
        `${lang.turnaround}`,
        "Bundle with MFA + Apostille",
      ]}
      whoFor={{
        th: [`Applicants filing with ${lang.en}-speaking authorities`, "International students", "Cross-border contract signatories"],
        foreign: [`Documents for ${lang.en}-speaking authorities`],
      }}
      steps={[
        { name: "Quote", text: "Send doc type + destination — quote in 30 min." },
        { name: "Sworn translation", text: `Registered ${lang.en} sworn translator` },
        { name: "4-eyes QA", text: "Second-linguist review" },
        { name: "Sworn seal", text: "Certificate of Accuracy included" },
        { name: "MFA/Apostille (optional)", text: "Bundle to save time" },
      ]}
      pricing={[
        { label: `Sworn translation ${lang.en}`, price: `${lang.priceFrom.toLocaleString()}/page`, note: lang.turnaround },
        { label: "Express 24h", price: "+50%", note: "subject to queue" },
        { label: "MFA legalization", price: "800/doc", note: "MFA seal" },
        { label: "Apostille (Hague)", price: "1,500/doc", note: "Hague member states" },
      ]}
      faqs={[
        { q: "Where to verify the registry?", a: lang.registryEn },
        { q: "Pricing and turnaround?", a: `From ฿${lang.priceFrom.toLocaleString()}/page, ${lang.turnaround}.` },
        { q: "Caveats?", a: lang.notes || "Verify destination requirements before ordering." },
        { q: "Can you add MFA + Apostille?", a: `Yes — Thai MFA legalisation and Apostille (Hague, 15 Dec 2025) can be added to any ${lang.en} sworn translation in a single workflow, saving time and courier costs compared with running the steps separately.` },
        { q: "Delivery?", a: `Free EMS anywhere in Thailand within 1–2 business days; DHL/FedEx Express to 220+ countries in 2–5 business days with full tracking. Digitally-signed PDFs of every ${lang.en} translation are emailed the same day at no extra cost.` },
      ]}
      relatedLinks={[
        { to: "/en/sworn-translator", label: "← Sworn Translator hub" },
        ...SWORN_LANGUAGES.filter((x) => x.slug !== lang.slug).slice(0, 5).map((x) => ({
          to: `/en/sworn-translator/${x.slug}` as const, label: `Sworn ${x.en}`,
        })) as any,
        { to: "/en/moj-translator", label: "MoJ Sworn Translator" },
      ]}
    />
  );
}
